import React from 'react';
import { CheckCircle2 } from 'lucide-react';

export const TermsPage: React.FC = () => {
  return (
    <div className="canvas-dark" style={{ minHeight: '100vh', paddingTop: '130px', paddingBottom: '80px', backgroundColor: '#121212' }}>
      
      {/* Hero */}
      <header className="container" style={{ padding: '0 20px 48px', maxWidth: '880px' }}>
        <div className="eyebrow" style={{ marginBottom: '12px' }}>Legal Agreement</div>
        <h1 
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: 'clamp(32px, 5vw, 48px)',
            fontWeight: 400,
            lineHeight: 1.2,
            letterSpacing: '-1px',
            color: '#FFFFFF',
            margin: '0 0 16px'
          }}
        >
          Terms of Service
        </h1>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: '15px', color: '#D7D7D7', lineHeight: 1.6, margin: 0 }}>
          Effective Date: January 1, 2026 &bull; Last Updated: September 2026. <br />
          These Terms of Service govern your access to and use of the Draway Auth cloud platform, APIs, SDKs, and developer tooling provided by Draway Inc.
        </p>
      </header>

      {/* Content Layout */}
      <div className="container" style={{ maxWidth: '880px', padding: '0 20px' }}>
        <div 
          style={{ 
            backgroundColor: '#1A1A1A', 
            border: '1px solid rgba(255, 255, 255, 0.08)', 
            borderRadius: '8px', 
            padding: '40px 36px', 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '36px' 
          }}
        >
          {/* Quick Summary Strip */}
          <div 
            style={{ 
              backgroundColor: '#121212', 
              border: '1px solid #374151', 
              borderRadius: '8px', 
              padding: '22px 24px' 
            }}
          >
            <h4 style={{ color: '#FFFFFF', fontSize: '15px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <CheckCircle2 size={18} color="#6D7CFF" /> Plain-English Developer Summary
            </h4>
            <ul style={{ color: '#D7D7D7', fontSize: '14px', marginTop: '12px', paddingLeft: '20px', lineHeight: 1.7 }}>
              <li><strong style={{ color: '#FFFFFF' }}>You own your data:</strong> Your user records, password hashes, and encryption seeds belong entirely to you. You can export them at any time.</li>
              <li><strong style={{ color: '#FFFFFF' }}>Zero lock-in:</strong> We do not hold password hashes hostage. 1-click export of Argon2id hashes is guaranteed on all plans.</li>
              <li><strong style={{ color: '#FFFFFF' }}>Transparent INR tiers:</strong> Free plan includes up to 50 users and 100 free SMS every month. Zero credit card required to start.</li>
              <li><strong style={{ color: '#FFFFFF' }}>Anti-dark-patterns:</strong> Zero silent auto-upgrades, no surprise cliffs, and 1-click self-serve cancellation.</li>
              <li><strong style={{ color: '#FFFFFF' }}>Security commitment:</strong> We maintain strict zero-knowledge architecture and 99.99% edge availability SLAs.</li>
            </ul>
          </div>

          {/* Section 1 */}
          <section>
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>1. Acceptance &amp; Scope of Agreement</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              By registering an account, initializing the <code>@draway/auth-sdk</code>, or sending requests to the Draway Auth API endpoints, you (&quot;Customer&quot;, &quot;Developer&quot;, or &quot;You&quot;) agree to be legally bound by these Terms of Service. If you are accepting on behalf of an entity, you represent and warrant that you have the authority to bind that entity to these Terms.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>2. Platform Services &amp; Multi-Tenancy</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              Draway Auth grants you a non-exclusive, worldwide, revocable license to integrate our authentication APIs, WebAuthn passkey infrastructure, multi-factor verification channels (WhatsApp, SMS, Email, TOTP), and multi-tenant domain routing into your applications. We commit to maintaining backwards-compatible API versions under semantic versioning principles.
            </p>
          </section>

          {/* Section 3 */}
          <section>
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>3. Subscription Tiers, Invoicing &amp; Telecom Add-ons</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              Draway Auth operates on transparent monthly billing in Indian Rupees (INR ₹) under five distinct tiers:
            </p>
            <ul style={{ color: '#D7D7D7', fontSize: '14px', marginTop: '10px', paddingLeft: '20px', lineHeight: 1.7 }}>
              <li><strong style={{ color: '#FFFFFF' }}>Free Tier (₹0.00 / month):</strong> Strictly up to 50 active users per monthly cycle. Includes 100 free carrier SMS messages per month. No credit card required.</li>
              <li><strong style={{ color: '#FFFFFF' }}>Standard Tier (₹799.00 / month):</strong> Includes up to 1,000 active users, custom domain routing (<code>auth.yourdomain.in</code>), and 1,000 free SMS messages per month.</li>
              <li><strong style={{ color: '#FFFFFF' }}>Pro Tier (₹1,599.00 / month):</strong> Includes up to 5,000 active users, 1 Enterprise SAML 2.0 / OIDC SSO connection, automated telephony toll-fraud circuit breakers, and 3,000 free SMS messages per month.</li>
              <li><strong style={{ color: '#FFFFFF' }}>Premium Tier (₹3,999.00 / month):</strong> Includes up to 15,000 active users, 5 SAML SSO connections, 99.99% Edge SLA, 10,000 free SMS messages per month, and private architect Slack support.</li>
              <li><strong style={{ color: '#FFFFFF' }}>Enterprise Tier (Custom Quotation):</strong> Tailored high-volume contracts, dedicated Cloud VPC or On-Premises Kubernetes cluster deployment, BYO carrier credentials, and 99.999% HA cluster SLAs.</li>
              <li><strong style={{ color: '#FFFFFF' }}>Carrier SMS Overage:</strong> Deliveries beyond plan allowance are billed at wholesale <strong style={{ color: '#FFFFFF' }}>₹0.99 (99 paise)</strong> per SMS.</li>
              <li><strong style={{ color: '#FFFFFF' }}>WhatsApp Business OTP Add-on:</strong> Available across all plans at <strong style={{ color: '#FFFFFF' }}>₹0.99 (99 paise)</strong> per delivered message via Meta Cloud API.</li>
              <li><strong style={{ color: '#FFFFFF' }}>Anti-Dark-Pattern Invariant:</strong> Draway Auth covenants never to silently bump customers into higher tiers without explicit affirmative consent. If an account approaches plan limits, warning notifications will be displayed within the console with options to scale or cap access.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>4. Customer Data Ownership &amp; 1-Click Export</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              You retain sole ownership of all data submitted to or processed by Draway Auth. We provide automated, self-serve export endpoints allowing you to download all user catalogs, metadata, and Argon2id password hashes with cryptographic salts at any time with zero termination penalties.
            </p>
          </section>

          {/* Section 5 */}
          <section id="sla">
            <h3 style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}>5. Service Level Agreement (SLA) &amp; Edge Uptime</h3>
            <p style={{ color: '#D7D7D7', fontSize: '14px', lineHeight: 1.7 }}>
              Draway Auth guarantees a 99.99% uptime SLA for public JWKS key verification endpoints and core authentication APIs. In the event of a qualifying outage exceeding SLA limits, eligible customers receive pro-rata service credits applied to their monthly invoice.
            </p>
          </section>

          {/* Contact Strip */}
          <div style={{ paddingTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.08)', fontSize: '13px', color: '#9CA3AF' }}>
            For legal inquiries, contractual amendments, or enterprise master service agreements, contact the Draway legal desk at <a href="mailto:legal@draway.in" style={{ color: '#6D7CFF', textDecoration: 'underline' }}>legal@draway.in</a>.
          </div>

        </div>
      </div>

    </div>
  );
};

export default TermsPage;
